import type { GameStorage, SaveSlot } from "./GameStorage";
import type { GameSave } from "./GameSave";
import { migrateSave } from "./migrations";

const DB_NAME = "kurio";
const STORE_NAME = "saves";
const CURRENT_KEY = "current";
const SLOTS_KEY = "slots";

interface SaveData {
  readonly slot: SaveSlot;
  readonly state: GameSave;
}

/** Persists save slots to IndexedDB. Pass a `factory` in tests (e.g. fake-indexeddb). */
export class IndexedDbGameStorage implements GameStorage {
  constructor(private readonly factory: IDBFactory = indexedDB) {}

  private openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = this.factory.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore(STORE_NAME);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Failed to open IndexedDB"));
    });
  }

  private async get<T>(key: string): Promise<T | undefined> {
    const db = await this.openDb();
    try {
      return await new Promise<T | undefined>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const request = tx.objectStore(STORE_NAME).get(key);
        request.onsuccess = () => resolve(request.result as T | undefined);
        request.onerror = () => reject(request.error ?? new Error(`Failed to read "${key}"`));
      });
    } finally {
      db.close();
    }
  }

  private async set<T>(key: string, value: T): Promise<void> {
    const db = await this.openDb();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).put(value, key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error(`Failed to write "${key}"`));
      });
    } finally {
      db.close();
    }
  }

  private async remove(key: string): Promise<void> {
    const db = await this.openDb();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error(`Failed to delete "${key}"`));
      });
    } finally {
      db.close();
    }
  }

  async load(): Promise<GameSave | null> {
    const data = await this.get<GameSave>(CURRENT_KEY);
    return data ? migrateSave(data) : null;
  }

  async loadSlot(id: string): Promise<GameSave | null> {
    const data = await this.get<SaveData>(`slot:${id}`);
    return data ? migrateSave(data.state) : null;
  }

  async save(state: GameSave, name?: string): Promise<SaveSlot> {
    const sceneId = state.world.currentSceneId;
    const slot: SaveSlot = {
      id: `save-${Date.now()}`,
      name: name ?? sceneId,
      timestamp: Date.now(),
      sceneId,
    };

    await this.set(CURRENT_KEY, state);
    await this.set(`slot:${slot.id}`, { slot, state } satisfies SaveData);

    const slots = await this.listSlots();
    const updated = [...slots.filter((s) => s.id !== slot.id), slot];
    await this.set(SLOTS_KEY, updated);

    return slot;
  }

  async autoSave(state: GameSave): Promise<void> {
    await this.set(CURRENT_KEY, state);
  }

  async listSlots(): Promise<readonly SaveSlot[]> {
    const slots = await this.get<SaveSlot[]>(SLOTS_KEY);
    return (slots ?? []).filter((s) => !s.auto);
  }

  async deleteSlot(id: string): Promise<void> {
    await this.remove(`slot:${id}`);
    const slots = await this.listSlots();
    await this.set(SLOTS_KEY, slots.filter((s) => s.id !== id));
  }

  async clear(): Promise<void> {
    const slots = await this.listSlots();
    for (const slot of slots) {
      await this.remove(`slot:${slot.id}`);
    }
    await this.remove(CURRENT_KEY);
    await this.remove(SLOTS_KEY);
  }
}
